import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { bugoDataAtomFamily } from '@shared/api/bugo/bugo.atom';
import { useBugoHook } from '@shared/api/bugo/bugo.hook';
import {
  isThanksPrintBoxSet,
  ThanksPrintConfig,
  ThanksPrintTemplate,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { Image } from '@shared/api/media/media.interface';

import { GiftThanksPrintPreview } from './GiftThanksPrintPreview';
import {
  consumeGiftInputApplyRequest,
  giftInputApplyRequestAtom,
} from '../../state/giftInputApply.atom';
import { isOwnedByBugo } from '../../state/giftInputOwner';
import { giftThanksPrintAtom } from '../../state/giftThanksPrint.atom';
import {
  clampThanksMessage,
  fillThanksMessage,
  getThanksSenderName,
  normalizeThanksPreset,
  parseThanksSender,
  templateMessagePresets,
  thanksSenderNameLimit,
  THANKS_FALLBACK_NAME,
  THANKS_MESSAGE_MAX_CHARS,
  THANKS_MESSAGE_PRESETS,
  THANKS_SENDER_SUFFIXES,
  ThanksSenderSuffix,
  thanksMessageCharCount,
} from '../../utils/thanksPrintMessage';

interface GiftThanksPrintSectionProps {
  thanksPrint: ThanksPrintConfig<Image>;
  brandId: string;
  bugoId: string | null;
  /** 대상 상품들이 허용하는 템플릿 _id 목록 (미지정 = 브랜드 전체 허용) */
  allowedTemplateIds?: string[];
  /** 모달 등에 임베드 시 상단 구분선/여백 제거 (주문/결제 수정용) */
  embedded?: boolean;
}

const MIN_SWIPE = 50;

export const GiftThanksPrintSection: FC<GiftThanksPrintSectionProps> =
  function GiftThanksPrintSection({
    thanksPrint,
    brandId,
    bugoId,
    allowedTemplateIds,
    embedded = false,
  }) {
    // 상주/고인 정보
    useBugoHook(bugoId ?? '', !bugoId);
    const bugo = useRecoilValue(bugoDataAtomFamily(bugoId ?? ''));
    const member = bugo?.member ?? null;
    // ### 치환용: 상주의 고인에 대한 관계 + 고인 성별
    const relationType = member?.relationType;
    const deceasedSex = bugo?.fevent?.deceasedInfo?.sex;

    const [selection, setSelection] = useRecoilState(giftThanksPrintAtom);

    // 노출 템플릿 (enabled !== false ∩ 상품별 허용 목록, order 순)
    const templates = useMemo<ThanksPrintTemplate<Image>[]>(() => {
      const allowed =
        allowedTemplateIds && allowedTemplateIds.length > 0
          ? new Set(allowedTemplateIds)
          : null;
      return (thanksPrint.templates ?? [])
        .filter(
          (t) => t.enabled !== false && (!allowed || (t._id && allowed.has(t._id))),
        )
        .slice()
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }, [thanksPrint.templates, allowedTemplateIds]);

    const [templateIndex, setTemplateIndex] = useState(0);
    const [presetIndex, setPresetIndex] = useState(0);
    const [message, setMessage] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isMessageModified, setIsMessageModified] = useState(false);
    const [senderName, setSenderName] = useState(THANKS_FALLBACK_NAME);
    const [senderSuffix, setSenderSuffix] = useState<ThanksSenderSuffix>('배상');
    const [ready, setReady] = useState(false);

    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    // 상주 기반 기본값 — "기본값으로 되돌리기"와 최초 진입에서 같은 값을 쓴다
    const memberSenderName = member ? getThanksSenderName(member) : null;
    const memberDefaultMessage = useMemo(
      () =>
        clampThanksMessage(
          fillThanksMessage(THANKS_MESSAGE_PRESETS[0] ?? '', relationType, deceasedSex),
        ),
      [relationType, deceasedSex],
    );

    // 초기화 1회: 보존된 값 우선(상품 무관 공통 내용), 없으면 member 기반 기본값.
    // 단, 보존값은 **같은 부고(상주)의 것일 때만** 쓴다 — 앞서 응대한 다른 상주의
    // 감사글이 다음 주문서에 그대로 뜨는 사고를 막는다. (state/giftInputOwner 참고)
    const initRef = useRef(false);
    useEffect(() => {
      if (initRef.current) return;

      if (selection && isOwnedByBugo(selection.ownerBugoId, bugoId)) {
        initRef.current = true;
        setMessage(selection.message);
        // 레거시 합성값/누락 suffix 방어적으로 정규화
        const parsed = parseThanksSender(selection.senderName, selection.senderSuffix);
        setSenderName(parsed.name);
        setSenderSuffix(parsed.suffix);
        setIsMessageModified(true);
        // 같은 브랜드면 이전에 고른 디자인(템플릿) 복원
        if (selection.brandId === brandId && selection.templateId) {
          const i = templates.findIndex((t) => t._id === selection.templateId);
          if (i >= 0) setTemplateIndex(i);
        }
        setReady(true);
        return;
      }

      if (member) {
        initRef.current = true;
        setMessage(memberDefaultMessage);
        setSenderName(getThanksSenderName(member));
        setReady(true);
      }
    }, [selection, member, brandId, bugoId, templates, memberDefaultMessage]);

    // 대리주문 패널에서 "이 감사글로 채우기"를 누른 경우 — 직원이 내용을 보고
    // 누른 값이므로 명의가 달라도(같은 상가 다른 가족의 주문) 그대로 적용한다.
    const applyRequest = useRecoilValue(giftInputApplyRequestAtom);
    useEffect(() => {
      const requested = applyRequest?.thanksPrint;
      if (!requested || !bugoId || applyRequest?.bugoId !== bugoId) return;
      if (!consumeGiftInputApplyRequest('thanks-apply', applyRequest.requestedAt)) return;
      initRef.current = true;
      setMessage(clampThanksMessage(requested.message));
      setIsMessageModified(true);
      if (requested.senderName) {
        const parsed = parseThanksSender(requested.senderName, undefined);
        setSenderName(parsed.name);
        setSenderSuffix(parsed.suffix);
      }
      if (requested.templateId) {
        const i = templates.findIndex((t) => t._id === requested.templateId);
        if (i >= 0) setTemplateIndex(i);
      }
      setReady(true);
    }, [applyRequest, bugoId, templates]);

    // 대리주문 패널의 "주문서 정리" — 지금 상주 기본값으로 되돌린다
    // (헤더의 "기본값으로" 버튼과 같은 동작, 확인은 패널에서 이미 받았다)
    useEffect(() => {
      if (!applyRequest?.resetToMemberDefault || !bugoId) return;
      if (applyRequest.bugoId !== bugoId) return;
      //* 상주 정보가 아직 안 왔으면 기다린다 (이름이 OOO로 굳어버리는 것 방지)
      //* — 소비 처리보다 먼저 확인해야 요청을 흘리지 않는다
      if (!memberSenderName) return;
      if (!consumeGiftInputApplyRequest('thanks-reset', applyRequest.requestedAt)) return;
      initRef.current = true;
      setMessage(memberDefaultMessage);
      setPresetIndex(0);
      setIsMessageModified(false);
      setIsEditing(false);
      setSenderName(memberSenderName);
      setSenderSuffix('배상');
      setReady(true);
    }, [applyRequest, bugoId, memberDefaultMessage, memberSenderName]);

    // 선택 인덱스 범위 보정
    useEffect(() => {
      if (templateIndex > templates.length - 1) setTemplateIndex(0);
    }, [templates.length, templateIndex]);

    const composedSender = `${senderName.trim() || THANKS_FALLBACK_NAME} ${senderSuffix}`;
    const selectedTemplate = templates[templateIndex];

    // 감사글 영역(messageBox)이 없는 템플릿 = 문구가 이미 인쇄된 카드 — 이름만 입력
    // (백엔드가 남기는 유령 박스 {align:'center'} 방어 — 좌표 유무로 판정)
    const hasMessageArea = isThanksPrintBoxSet(selectedTemplate?.messageBox);

    // 선택 템플릿의 지정 문구 세트 — 비어있지 않으면 자유입력 불가, 이 중 택1
    const fixedPresets = useMemo(
      () => templateMessagePresets(selectedTemplate),
      [selectedTemplate],
    );
    const hasFixedPresets = fixedPresets.length > 0;
    // 이름 입력 최대 글자수 (인쇄 발신문구 전체 제한에서 발신표현 몫을 뺀 값)
    const nameLimit = thanksSenderNameLimit(selectedTemplate);

    // 이 상주 기준 기본값으로 되돌리기 —
    // 대리주문으로 여러 상주를 응대하거나 옆 패널에서 다른 가족의 주문 내용을
    // 담아온 뒤, 지금 상주에게 맞는 내용으로 한 번에 정리하는 탈출구.
    //* 발신 표현(배상/드림/올림)은 고르는 값이라 판단에서 제외 — 이름·감사글만 본다
    const canRestoreDefault =
      !!memberSenderName &&
      (senderName.trim() !== memberSenderName ||
        (hasMessageArea && !hasFixedPresets && message !== memberDefaultMessage));

    const handleRestoreDefault = useCallback(() => {
      if (!memberSenderName) return;
      const confirmed = window.confirm(
        `감사글과 보내는 이를 ${memberSenderName}님 기본 내용으로 되돌릴까요?\n(지금 입력된 내용은 지워집니다)`,
      );
      if (!confirmed) return;
      setMessage(memberDefaultMessage);
      setPresetIndex(0);
      setIsMessageModified(false);
      setIsEditing(false);
      setSenderName(memberSenderName);
      setSenderSuffix('배상');
    }, [memberSenderName, memberDefaultMessage]);

    // 지정 문구 템플릿으로 오면 세트 밖 감사글(자유입력/타 템플릿 문구)을 첫 문구로 보정
    // (감사글 영역이 없는 템플릿은 message를 쓰지 않으므로 보존값을 건드리지 않음)
    useEffect(() => {
      if (!ready || !hasMessageArea || fixedPresets.length === 0) return;
      const matched = fixedPresets.some(
        (p) => normalizeThanksPreset(p) === normalizeThanksPreset(message),
      );
      if (matched) return;
      setMessage(fixedPresets[0] ?? '');
      setIsEditing(false);
      setIsMessageModified(false);
    }, [ready, hasMessageArea, fixedPresets, message]);

    // 이름 제한이 있는 템플릿으로 전환 시 이미 입력된 초과분을 잘라 서버 거부 방지
    useEffect(() => {
      if (!ready || nameLimit === undefined) return;
      if (senderName.length > nameLimit) setSenderName(senderName.slice(0, nameLimit));
    }, [ready, nameLimit, senderName]);

    // 입력값을 atom에 보존 (주문하지 않아도 유지 → 같은 상주의 다른 상품에서도 이어 사용).
    // 어느 상주(부고)의 입력인지 도장을 남겨야 다른 상주 주문서로 새어나가지 않는다.
    useEffect(() => {
      if (!ready) return;
      setSelection({
        message,
        senderName,
        senderSuffix,
        brandId,
        templateId: selectedTemplate?._id,
        ownerBugoId: bugoId ?? undefined,
      });
    }, [
      ready,
      message,
      senderName,
      senderSuffix,
      brandId,
      bugoId,
      selectedTemplate,
      setSelection,
    ]);

    // 편집 시 textarea 높이 자동조절
    const adjustHeight = useCallback(() => {
      const el = textareaRef.current;
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    }, []);
    useEffect(() => {
      if (isEditing) adjustHeight();
    }, [isEditing, message, adjustHeight]);

    const handlePresetSelect = (idx: number) => {
      setPresetIndex(idx);
      setMessage(
        clampThanksMessage(
          fillThanksMessage(THANKS_MESSAGE_PRESETS[idx] ?? '', relationType, deceasedSex),
        ),
      );
      setIsMessageModified(false);
    };

    const onTouchStart = (e: React.TouchEvent) => {
      setTouchEnd(null);
      setTouchStart(e.targetTouches[0]?.clientX ?? null);
    };
    const onTouchMove = (e: React.TouchEvent) => {
      setTouchEnd(e.targetTouches[0]?.clientX ?? null);
    };
    const onTouchEnd = () => {
      if (touchStart === null || touchEnd === null) return;
      const distance = touchStart - touchEnd;
      if (distance > MIN_SWIPE) {
        setTemplateIndex((p) => (p === templates.length - 1 ? 0 : p + 1));
      } else if (distance < -MIN_SWIPE) {
        setTemplateIndex((p) => (p === 0 ? templates.length - 1 : p - 1));
      }
    };

    if (templates.length === 0) return null;

    const charCount = thanksMessageCharCount(message);

    return (
      <div
        className={embedded ? 'px-4 pb-6 pt-1' : 'border-t-8 border-gray-50 px-4 py-6'}
      >
        {/* 헤더 */}
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-stone-100 text-base">
            ✉️
          </span>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-stone-800">감사 메시지 인쇄</h3>
            <p className="truncate text-xs text-stone-500">
              감사글을 정성스럽게 인쇄해 함께 보내드려요
            </p>
          </div>
          {/* 지금 상주에게 맞는 기본 내용으로 정리 (다른 상주·가족 내용이 남았을 때) */}
          {canRestoreDefault && (
            <button
              type="button"
              onClick={handleRestoreDefault}
              title={`${memberSenderName}님 기본 감사글·보내는 이로 되돌립니다`}
              className="ml-auto shrink-0 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-500 active:bg-gray-100"
            >
              기본값으로
            </button>
          )}
        </div>

        <div className="mt-5 space-y-6">
          {thanksPrint.guideText && (
            <p className="whitespace-pre-line break-keep rounded-lg bg-stone-50 px-3 py-2.5 text-[13px] leading-relaxed text-stone-600">
              {thanksPrint.guideText}
            </p>
          )}

          {/* 1. 디자인 선택 + 라이브 미리보기 (스와이프) */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-100 text-[11px] font-bold text-stone-600">
                  1
                </span>
                디자인 선택
                {templates.length > 1 && (
                  <span className="text-xs font-normal text-gray-400">
                    (좌우로 넘겨보세요)
                  </span>
                )}
              </p>
              {templates.length > 1 && (
                <span className="text-xs font-medium text-gray-400">
                  {templateIndex + 1} / {templates.length}
                </span>
              )}
            </div>

            <div className="relative">
              <GiftThanksPrintPreview
                template={selectedTemplate}
                message={message}
                sender={composedSender}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              />

              {templates.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      setTemplateIndex((p) => (p === 0 ? templates.length - 1 : p - 1))
                    }
                    className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/25 p-2 text-white backdrop-blur-sm"
                  >
                    ◀
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      setTemplateIndex((p) => (p === templates.length - 1 ? 0 : p + 1))
                    }
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/25 p-2 text-white backdrop-blur-sm"
                  >
                    ▶
                  </button>
                </>
              )}
            </div>

            {templates.length > 1 && (
              <div className="mt-2.5 flex items-center justify-center gap-1.5">
                {templates.map((t, idx) => (
                  <button
                    key={t._id ?? idx}
                    type="button"
                    onClick={() => setTemplateIndex(idx)}
                    className={`h-1.5 rounded-full transition-all ${
                      idx === templateIndex ? 'w-5 bg-stone-500' : 'w-1.5 bg-stone-200'
                    }`}
                    aria-label={`템플릿 ${idx + 1}`}
                  />
                ))}
              </div>
            )}
            {selectedTemplate?.name && (
              <p className="mt-1 text-center text-xs text-gray-500">
                {selectedTemplate.name}
              </p>
            )}
          </div>

          {/* 2. 감사글 선택 — 감사글 영역이 없는 카드(문구 인쇄됨)는 통째로 숨김 */}
          {hasMessageArea && (
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-100 text-[11px] font-bold text-stone-600">
                2
              </span>
              감사글 선택
            </p>
            {hasFixedPresets ? (
              // 지정 문구 세트 — 이 카드에 준비된 문구 중 하나만 선택 (자유입력 불가)
              <div>
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                  {fixedPresets.map((preset, idx) => {
                    const isActive =
                      normalizeThanksPreset(preset) === normalizeThanksPreset(message);
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setMessage(preset);
                          setIsMessageModified(false);
                        }}
                        className={`w-40 shrink-0 rounded-xl border p-3 text-left text-xs leading-snug transition-colors ${
                          isActive
                            ? 'border-stone-400 bg-stone-50 text-gray-900 ring-1 ring-stone-200'
                            : 'border-gray-200 bg-white text-gray-500'
                        }`}
                      >
                        <span className="line-clamp-4 whitespace-pre">{preset}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-400">
                  이 카드는 준비된 문구 중에서 선택할 수 있어요.
                </p>
              </div>
            ) : (
              <>
                <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                  {THANKS_MESSAGE_PRESETS.map((preset, idx) => {
                    const filled = fillThanksMessage(preset, relationType, deceasedSex);
                    const isActive = idx === presetIndex && !isMessageModified;
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => handlePresetSelect(idx)}
                        className={`w-40 shrink-0 rounded-xl border p-3 text-left text-xs leading-snug transition-colors ${
                          isActive
                            ? 'border-stone-400 bg-stone-50 text-gray-900 ring-1 ring-stone-200'
                            : 'border-gray-200 bg-white text-gray-500'
                        }`}
                      >
                        <span className="line-clamp-4 whitespace-pre-line break-keep">
                          {filled}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* 선택된 감사글 — 탭하여 직접 수정 (4줄 / 100자 제한) */}
                <div className="mt-3">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-500">
                      내용을 직접 수정할 수 있어요 (4줄 이내)
                    </span>
                    {isEditing ? (
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="rounded-md bg-stone-600 px-2.5 py-1 text-xs font-medium text-white"
                      >
                        완료
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600"
                      >
                        수정
                      </button>
                    )}
                  </div>
                  {isEditing ? (
                    <textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => {
                        setMessage(clampThanksMessage(e.target.value));
                        setIsMessageModified(true);
                      }}
                      onInput={adjustHeight}
                      autoFocus
                      className="noto-serif-kr w-full resize-none rounded-xl border border-stone-400 bg-white px-3 py-2.5 text-base leading-relaxed text-gray-900 outline-none"
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => setIsEditing(true)}
                      className="noto-serif-kr block w-full whitespace-pre-line break-keep rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-left text-base leading-relaxed text-gray-800"
                    >
                      {message}
                    </button>
                  )}
                  <div className="mt-1 text-right text-xs text-gray-400">
                    {charCount}/{THANKS_MESSAGE_MAX_CHARS}자
                  </div>
                </div>
              </>
            )}
          </div>
          )}

          {/* 보내는 이 (감사글 영역이 없으면 2번째 단계) */}
          <div>
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-stone-100 text-[11px] font-bold text-stone-600">
                {hasMessageArea ? 3 : 2}
              </span>
              보내는 이
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={senderName}
                onChange={(e) =>
                  setSenderName(
                    nameLimit !== undefined
                      ? e.target.value.slice(0, nameLimit)
                      : e.target.value,
                  )
                }
                maxLength={nameLimit}
                placeholder="상주 이름"
                className="min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-2.5 text-sm outline-none focus:border-stone-500"
              />
              <div className="flex shrink-0 overflow-hidden rounded-xl border border-gray-300">
                {THANKS_SENDER_SUFFIXES.map((suffix) => (
                  <button
                    key={suffix}
                    type="button"
                    onClick={() => setSenderSuffix(suffix)}
                    className={`px-3 py-2.5 text-sm font-medium transition-colors ${
                      senderSuffix === suffix
                        ? 'bg-stone-600 text-white'
                        : 'bg-white text-gray-500'
                    }`}
                  >
                    {suffix}
                  </button>
                ))}
              </div>
            </div>
            <p className="mt-1.5 text-xs text-gray-400">
              인쇄 시{' '}
              <b className="font-semibold text-gray-600 underline decoration-1 underline-offset-2">
                {composedSender}
              </b>{' '}
              형태로 출력됩니다.
              {nameLimit !== undefined && (
                <> 이 카드는 이름을 최대 {nameLimit}자까지 인쇄할 수 있어요.</>
              )}
            </p>
          </div>
        </div>
      </div>
    );
  };
